import { cssBundleHref } from '@remix-run/css-bundle';
import {
  isRouteErrorResponse,
  Link,
  Links,
  LiveReload,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useNavigation,
  useRouteError } from
'@remix-run/react';
import {
  json,
  LinksFunction,
  LoaderFunctionArgs } from
'@remix-run/server-runtime';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css?__remix_sideEffect__";
import { useChangeLanguage } from 'remix-i18next';
import { CartTray } from '~/components/cart/CartTray';
import Footer from '~/components/footer/Footer';
import {
  APP_META_DESCRIPTION,
  APP_META_TITLE,
  DEMO_API_URL } from
'~/constants';
import { getI18NextServer } from '~/i18next.server';
import { activeChannel } from '~/providers/channel/channel';
import { getCollections } from '~/providers/collections/collections';
import { getActiveCustomer } from '~/providers/customer/customer';
import { useActiveOrder } from '~/utils/use-active-order';
import { Header } from './components/header/Header';
import { ErrorCode, OrderDetailFragment } from './generated/graphql';
import { getAllPages } from './providers/pages/pages';
import stylesheet from './tailwind.css';
import { clearAllSessionData } from '~/utils/auth-helper';
import {
  getMerchantContextState,
  MerchantContextState } from
'~/providers/merchant-context/merchant-context';

declare global {
  interface Window {
    ENV: {
      VENDURE_API_URL: string;
      GOOGLE_ANALYTICS_ID?: string;
    };
    gtag: (...args: any[]) => void;
  }
}

export const meta: MetaFunction = () => {
  return [{ title: APP_META_TITLE }, { description: APP_META_DESCRIPTION }];
};

export const links: LinksFunction = () => [
{ rel: 'stylesheet', href: stylesheet },
...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : [])];


const devMode =
typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

// The root data does not change once loaded.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  nextUrl,
  currentUrl,
  formAction,
  formMethod,
  defaultShouldRevalidate
}) => {
  if (currentUrl.pathname === '/sign-in') {
    // just logged in
    return true;
  }
  if (currentUrl.pathname === '/account' && nextUrl.pathname === '/') {
    // just logged out
    return true;
  }
  if (formAction === '/checkout/payment') {
    // submitted payment for order
    return true;
  }
  if (formMethod && formMethod.toUpperCase() !== 'GET') {
    return defaultShouldRevalidate;
  }
  return false;
};

export type RootLoaderData = {
  activeCustomer: Awaited<ReturnType<typeof getActiveCustomer>>;
  activeChannel: Awaited<ReturnType<typeof activeChannel>>;
  collections: Awaited<ReturnType<typeof getCollections>>;
  locale: string;
  pages?: Awaited<ReturnType<typeof getAllPages>>;
  merchantContext: MerchantContextState;
  env: {
    VENDURE_API_URL: string;
    GOOGLE_ANALYTICS_ID?: string;
  };
};

export async function loader({ request }: LoaderFunctionArgs) {
  const [
  pages,
  collections,
  activeCustomer,
  activeChannelData,
  locale,
  merchantContext] =
  await Promise.all([
  getAllPages({ take: 5 }, request),
  getCollections(request, { take: 100 }),
  getActiveCustomer({ request }),
  activeChannel({ request }),
  getI18NextServer().then((i18next) => i18next.getLocale(request)),
  getMerchantContextState(request).catch(() => ({
    capability: {
      canSwitchMerchant: false,
      isSuperAdmin: false,
      role: null,
      requestId: 'unavailable'
    },
    merchants: [],
    activeMerchant: null
  }))]
  );
  const topLevelCollections = collections.filter(
    (collection) => collection.parent?.name === '__root_collection__'
  );

  const loaderData: RootLoaderData = {
    activeCustomer,
    activeChannel: activeChannelData,
    collections: topLevelCollections,
    locale,
    pages: pages,
    merchantContext,
    env: {
      VENDURE_API_URL: process.env.VENDURE_API_URL || DEMO_API_URL,
      GOOGLE_ANALYTICS_ID: process.env.GOOGLE_ANALYTICS_ID
    }
  };

  return json(loaderData, {
    headers: {
      ...activeCustomer._headers,
      'Cache-Control': 'private, no-store'
    }
  });
}

export default function App() {
  const [open, setOpen] = useState(false);
  const loaderData = useLoaderData<RootLoaderData>();
  const { pages } = loaderData;
  const { locale } = useLoaderData<typeof loader>();
  const { i18n } = useTranslation();
  const {
    activeOrderFetcher,
    activeOrder,
    adjustOrderLine,
    removeItem,
    refresh
  } = useActiveOrder();
  const location = useLocation();

  const navigation = useNavigation();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    // Artificial delay to ensure loading bar is visible on F5
    const timer = setTimeout(() => {
      setInitialLoad(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const isLoading = navigation.state !== 'idle' || initialLoad;

  // Track the last toast message to prevent duplicates but allow new messages
  const lastToastMessageRef = useRef<string | null>(null);
  const lastFetcherStateRef = useRef<string>('idle');

  useChangeLanguage(locale);

  useEffect(() => {
    // Handle errors from URL parameters (e.g. SSO failure)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const errorParam = url.searchParams.get('error');
      if (errorParam) {
        toast.error(`Authentication Error: ${decodeURIComponent(errorParam)}`);

        // Clear session data on authentication error to prevent persistent broken state
        clearAllSessionData();

        // Clean up the URL to remove the error param
        url.searchParams.delete('error');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, []);

  useEffect(() => {
    // When the loader has run, this implies we should refresh the contents
    // of the activeOrder as the user may have signed in or out.
    refresh();
  }, [loaderData]);

  useEffect(() => {
    if (
    loaderData.env.GOOGLE_ANALYTICS_ID &&
    typeof window.gtag === 'function')
    {
      window.gtag('config', loaderData.env.GOOGLE_ANALYTICS_ID, {
        page_path: location.pathname
      });
    }
  }, [location, loaderData.env.GOOGLE_ANALYTICS_ID]);

  useEffect(() => {
    const data = (activeOrderFetcher.data as
    {
      error?: {errorCode?: string;message?: string;};
      successMessage?: string;
    } |
    undefined);
    const error = data?.error;
    const successMessage = data?.successMessage;
    const currentState = activeOrderFetcher.state;

    // Handle success messages - show toast when fetcher completes
    if (
    successMessage &&
    currentState === 'idle' &&
    lastFetcherStateRef.current !== 'idle')
    {
      // Only show if it's a different message OR if the fetcher just completed
      if (successMessage !== lastToastMessageRef.current) {
        toast.success(successMessage, {
          position: 'top-left',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastToastMessageRef.current = successMessage;
      }
    }

    // Reset last message when starting a new operation
    if (currentState === 'submitting') {
      lastToastMessageRef.current = null;
    }

    // Track fetcher state changes
    lastFetcherStateRef.current = currentState;

    // Handle errors
    if (!error || !error.errorCode) {
      return;
    }

    if (error.message) {
      toast.error(error.message);
    }
  }, [activeOrderFetcher.data, activeOrderFetcher.state]);

  return (
    <html lang={locale} dir={i18n.dir()} id="app">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous" />

        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet" />

        <link
          rel="stylesheet"
          href="https://maxst.icons8.com/vue-static/landings/line-awesome/line-awesome/1.3.0/css/line-awesome.min.css" />

        <Meta />
        <Links />
        {loaderData.env.GOOGLE_ANALYTICS_ID &&
        <>
            <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${loaderData.env.GOOGLE_ANALYTICS_ID}`} />

            <script
            dangerouslySetInnerHTML={{
              __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${loaderData.env.GOOGLE_ANALYTICS_ID}', {
                    page_path: window.location.pathname,
                  });
                `
            }} />

          </>}

      </head>
      <body>
        {isLoading &&
        <div className="fixed top-0 left-0 z-[9999] w-full h-1 bg-transparent">
            <div className="h-full w-full bg-red-50 overflow-hidden relative">
              <div className="absolute top-0 left-0 h-full bg-primary animate-progress-indeterminate"></div>
            </div>
          </div>}

        <Header
          onCartIconClick={() => setOpen(!open)}
          cartQuantity={activeOrder?.totalQuantity ?? 0} />

        {loaderData.merchantContext.capability.canSwitchMerchant &&
        !loaderData.merchantContext.activeMerchant &&
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-900">
              Select a merchant in the header before adding items or checking
              out.
            </div>}

        <main className="">
          <Outlet
            context={{
              activeOrderFetcher,
              activeOrder,
              adjustOrderLine,
              removeItem,
              merchantContextRequired:
              loaderData.merchantContext.capability.canSwitchMerchant &&
              !loaderData.merchantContext.activeMerchant
            }} />

        </main>
        <CartTray
          open={open}
          onClose={setOpen}
          activeOrder={(activeOrder as OrderDetailFragment)}
          adjustOrderLine={adjustOrderLine}
          removeItem={removeItem} />

        <ToastContainer
          position="top-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light" />

        <Footer pages={pages || []}></Footer>
        <ScrollRestoration />
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(loaderData.env)}`
          }} />

        {devMode && <LiveReload />}
      </body>
    </html>);

}

type DefaultSparseErrorPageProps = {
  tagline: string;
  headline: string;
  description: string;
};

/**
 * You should replace this in your actual storefront to provide a better user experience.
 * You probably want to still show your footer and navigation. You will also need fallbacks
 * for your data dependant components in case your shop instance / CMS isnt responding.
 * See: https://remix.run/docs/en/main/route/error-boundary
 */
function DefaultSparseErrorPage({
  tagline,
  headline,
  description
}: DefaultSparseErrorPageProps) {
  return (
    <html lang="en" id="app">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="icon" href="/favicon.ico" type="image/png"></link>
        <Meta />
        <Links />
      </head>
      <body>
        <main className="flex flex-col items-center px-4 py-16 sm:py-32 text-center">
          <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            {tagline}
          </span>
          <h1 className="mt-2 font-bold text-gray-900 tracking-tight text-4xl sm:text-5xl">
            {headline}
          </h1>
          <p className="mt-4 text-base text-gray-500 max-w-full break-words">
            {description}
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="text-base font-medium text-primary/90 hover:text-primary/80 inline-flex gap-2">

              Go back home
            </Link>
          </div>
        </main>
        <ScrollRestoration />
        <Scripts />
        {devMode && <LiveReload />}
      </body>
    </html>);

}

/**
 * As mentioned in the jsdoc for `DefaultSparseErrorPage` you should replace this to suit your needs.
 */
export function ErrorBoundary() {
  let tagline = 'Oopsy daisy';
  let headline = 'Unexpected error';
  let description = "We couldn't handle your request. Please try again later.";

  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    tagline = `${error.status} error`;
    headline = error.statusText;
    description = error.data;
  }

  return (
    <DefaultSparseErrorPage
      tagline={tagline}
      headline={headline}
      description={description} />);


}

/**
 * In Remix v2 there will only be a `ErrorBoundary`
 * As mentioned in the jsdoc for `DefaultSparseErrorPage` you should replace this to suit your needs.
 * Relevant for the future: https://remix.run/docs/en/main/route/error-boundary-v2
 */
export function CatchBoundary() {
  return ErrorBoundary();
}